import { ActionCreator, AnyAction, Dispatch } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { ApplicationState } from 'store/rootReducer';
import * as actionTypes from 'constants/action-types';
import axios from 'axios/axios-base';
import { cacheControlHeaders, forceBrowserToDownloadData } from 'utils/miscUtil';
import { applySearchTermFilterToListWithTags } from 'utils/filterUtils';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import i18n from 'i18n';
import { MarFilesActionTypes } from 'store/marFiles/marFilesActionTypes';
import { action } from 'typesafe-actions';
import MixpanelService from 'utils/mixpanelService';
import { DELETE_MAR, DOWNLOAD_MAR, MAR_FILES_BULK_OPERATION, OperationStatus } from 'utils/mixpanelEvents';
import { MarAsset } from 'types/rest/marFilesTypes';
import { buildPatchBody } from 'utils/assetUtils';
import _ from 'lodash';
import { isBlazeVseFeatureEnabled, isDevTestFeatureEnabled } from 'utils/bzmUserConfigUtil';
import { MockServiceActionTypes } from 'store/mockServices/mockServiceActionTypes';
import acApi from 'axios/ac/acApi';
import { displayErrorForBulkOpsThunk } from '../transactions/transactionsActions';

export const getMarFileListThunk: ActionCreator<ThunkAction<any, ApplicationState, null, MarFilesActionTypes>> = (
  postMarFilesLoadMessage?: string,
): ThunkAction<any, ApplicationState, null, MarFilesActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>, getState: any) => {
    if (
      isDevTestFeatureEnabled(getState().blazeUserConfig.blazeUserConfig) ||
      isBlazeVseFeatureEnabled(getState().blazeUserConfig.blazeUserConfig)
    ) {
      try {
        // Get active service id from the redux
        let serviceId = null;
        const pageFilter = getState().ui.pageFilter;
        const selectedServiceId = pageFilter.serviceId;
        if (selectedServiceId && selectedServiceId > 0) {
          serviceId = selectedServiceId;
        }

        dispatch({ type: actionTypes.LOADING_MAR_FILES_LIST, payload: { displayLoading: true } });
        const response = await axios.get(`/workspaces/{workspaceId}/assets?includeEndpoints=true`, {
          headers: cacheControlHeaders,
          params: {
            serviceId: serviceId,
            type: 'MAR',
            orderBy: '-id',
          },
        });

        // Filter results by inline filter if it is enabled (Backend filtering is not working right now)
        const marFiles = applySearchTermFilterToListWithTags(response.data.result, pageFilter.searchTerm);

        dispatch({
          type: actionTypes.VIEW_MAR_FILES,
          payload: marFiles,
        });

        if (!_.isEmpty(postMarFilesLoadMessage) && postMarFilesLoadMessage != null) {
          dispatch(showToastNotification(ToastType.Success, postMarFilesLoadMessage));
        }
      } catch (err) {
        dispatch({ type: actionTypes.LOADING_MAR_FILES_LIST, payload: { displayLoading: false } });
        dispatch(
          showToastNotification(
            ToastType.Danger,
            i18n.t('marFiles:errorMsgs.networkProblem'),
            i18n.t('marFiles:errorMsgs.cannotLoadMarFiles'),
          ),
        );
      }
    }
  };
};

export const setFilterAction = (
  serviceId: number,
  searchTerm: string,
  onFilterChange?: any,
): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return (dispatch: Dispatch) => {
    dispatch(
      action(actionTypes.ASSET_FILTER_CHANGED, {
        serviceId: serviceId,
        searchTerm: searchTerm,
      }),
    );

    if (onFilterChange) {
      onFilterChange();
    }
  };
};

export const deleteMarFileThunk: ActionCreator<ThunkAction<any, ApplicationState, null, MarFilesActionTypes>> = (
  marFile: MarAsset,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    try {
      dispatch({ type: actionTypes.LOADING_MAR_FILES_LIST, payload: { displayLoading: true } });
      const response = await acApi.assets.delete(marFile.id, cacheControlHeaders);

      if (response.status === 204)
        dispatch(
          showToastNotification(
            ToastType.Success,
            i18n.t('marFiles:successMsgs.deleteSuccessMsg', { name: marFile.name }),
          ),
        );
      MixpanelService.track(DELETE_MAR(marFile.name, OperationStatus.SUCCESS));
      dispatch({
        type: actionTypes.DELETE_MAR_FILE,
        payload: marFile,
      });
    } catch (err) {
      dispatch({ type: actionTypes.LOADING_MAR_FILES_LIST, payload: { displayLoading: false } });
      MixpanelService.track(DELETE_MAR(marFile.name, OperationStatus.ERROR, err));
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('marFiles:errorMsgs.networkProblem'),
          i18n.t('marFiles:errorMsgs.failedToDeleteMarFile', { name: marFile.name }),
        ),
      );
    }
  };
};

export const downloadMarThunk: ActionCreator<ThunkAction<any, ApplicationState, null, MarFilesActionTypes>> = (
  marFile: MarAsset,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<ApplicationState, any, AnyAction>) => {
    try {
      const response = await acApi.assets.download(marFile.id);
      if (response.status === 200) {
        MixpanelService.track(DOWNLOAD_MAR(marFile.name, OperationStatus.SUCCESS));
        dispatch(
          showToastNotification(
            ToastType.Success,
            i18n.t('marFiles:successMsgs.downloadSuccessMsg', { name: marFile.name }),
          ),
        );
        if (response.data.result) {
          const responseFile = await axios.get(response.data.result, {
            responseType: 'blob',
          });
          forceBrowserToDownloadData(responseFile.data, marFile.name);
        }
      }
      dispatch({
        type: actionTypes.DOWNLOAD_MAR_FILE,
        payload: marFile,
      });
    } catch (err) {
      MixpanelService.track(DOWNLOAD_MAR(marFile.name, OperationStatus.ERROR, err));
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('marFiles:errorMsgs.networkProblem'),
          i18n.t('marFiles:errorMsgs.failedToDownloadMarFile', { name: marFile.name }),
        ),
      );
    }
  };
};

/**
 * Detects duplicates of Mar file assets by name
 * @param serviceId
 * @param marName name of the mar file
 * @param idToIgnore id of the asset to be ignored from results, usually it is the current one
 */
export const detectDuplicates = (serviceId: number, marName: string, idToIgnore?: number) => {
  return (async () => {
    const response = await axios.post(
      `/workspaces/{workspaceId}/assets/validate-name`,
      {
        serviceId: serviceId,
        name: marName,
        idToIgnore: idToIgnore,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    return !response.data.result;
  })();
};

export const updateAssetThunk: ActionCreator<ThunkAction<any, ApplicationState, null, MarFilesActionTypes>> = (
  asset: MarAsset,
  fnCallbackOnSuccess?: any,
  allowOverride?: string,
): ThunkAction<any, ApplicationState, null, MockServiceActionTypes> => {
  return async (dispatch: ThunkDispatch<any, any, AnyAction>) => {
    try {
      dispatch({ type: actionTypes.LOADING_MAR_FILES_LIST, payload: { displayLoading: true } }); //Won't show if the duplicate MAR modal is open

      let duplicateDetected;

      if (!allowOverride) {
        duplicateDetected = await detectDuplicates(asset.serviceId, asset.name, asset.id);
      } else {
        dispatch({ type: actionTypes.LOADING_MAR_FILES_LIST, payload: { displayLoading: true } }); //Need this here to show the loading mask after closing the duplicate MAR modal
      }

      if (duplicateDetected) {
        dispatch({
          type: actionTypes.DUPLICATE_ASSET_DETECTED,
          payload: {
            found: true,
            asset: asset,
          },
        });
      } else {
        if (!allowOverride) {
          allowOverride = '';
        }
        const patchBody = buildPatchBody(asset);
        await axios.patch(`/workspaces/{workspaceId}/assets/${asset.id}?handleSameName=${allowOverride}`, patchBody);

        dispatch(
          showToastNotification(
            ToastType.Success,
            i18n.t('marFiles:successMsgs.updateSuccessMsg', { name: asset.name }),
          ),
        );
        if (fnCallbackOnSuccess) {
          fnCallbackOnSuccess(asset);
        }

        return dispatch(getMarFileListThunk());
      }
    } catch (err) {
      dispatch({ type: actionTypes.LOADING_MAR_FILES_LIST, payload: { displayLoading: false } });
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('marFiles:errorMsgs.networkProblem'),
          i18n.t('marFiles:errorMsgs.failedToSaveMarFile', { name: asset.name }),
        ),
      );
    }
  };
};

export const bulkDelete = (marFilesIds: number[], unSelectAll: any): ThunkAction<any, any, any, AnyAction> => {
  return async (dispatch: ThunkDispatch<any, any, AnyAction>) => {
    try {
      dispatch({ type: actionTypes.LOADING_MAR_FILES_LIST, payload: { displayLoading: true } });

      const response = await acApi.assets.deleteBulk(marFilesIds);

      if (response.data.error) {
        dispatch({ type: actionTypes.LOADING_MAR_FILES_LIST, payload: { displayLoading: false } });
        displayErrorForBulkOpsThunk(response, i18n.t('marFiles:actions.failedToDeleteMarFiles'), dispatch);
      } else {
        //Track Mixpanel event
        MixpanelService.track(MAR_FILES_BULK_OPERATION('delete', marFilesIds.length));

        return dispatch(
          getMarFileListThunk(
            `${i18n.t('marFiles:actions.successfullyDeleted', { numberOfMarFiles: marFilesIds.length })}`,
          ),
        );
      }
    } catch (e) {
      dispatch({ type: actionTypes.LOADING_MAR_FILES_LIST, payload: { displayLoading: false } });
      const errorMsg = _.get(e, 'response.data.error', i18n.t('marFiles:actions.somethingWentWrong'));
      dispatch(
        showToastNotification(
          ToastType.Danger,
          `${i18n.t('marFiles:actions.failedToDeleteSelectedMarFiles')}  ${errorMsg}`,
        ),
      );
    } finally {
      unSelectAll();
    }
  };
};

/**
 * Toggles expansion of the row in mar files table.
 * @param assetId
 */
export const toggleExpandedMarFilesRow = (assetId: number | null) =>
  action(actionTypes.MAR_FILES_TOGGLE_EXPAND_ROW, assetId);
